// ============================================================================
// NOTICE TEMPLATE FORM MODAL — Add / Edit Notice Template (MSTR-NTMP-001)
// ============================================================================
// XL 1100px. Two cascade FKs (Notice Type mandatory, Action Type optional) +
// Body HTML textarea. Uses seedOptions to preserve labels for inactive parents.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { noticeTemplateService } from 'api/services/noticeTemplateService';
import { noticeTypeService } from 'api/services/noticeTypeService';
import { actionTypeService } from 'api/services/actionTypeService';
import type { NoticeTemplate, ApplicableCategory } from 'api/types/noticeTemplate.types';
import { useCaseTypeOptions } from 'hooks/api/useCaseTypes';

type TemplateStatus = 'ACTIVE' | 'INACTIVE';
type SelectOption = { value: string; label: string };

interface NoticeTemplateFormData {
  templateCode: string;
  templateName: string;
  languageCode: string;
  applicableCategory: ApplicableCategory;
  noticeTypeId: string;
  actionTypeId: string;
  subject: string;
  bodyHtml: string;
  placeholders: string;
  statusCode: TemplateStatus;
}

const INITIAL_FORM_DATA: NoticeTemplateFormData = {
  templateCode: '',
  templateName: '',
  languageCode: 'EN',
  applicableCategory: 'PRE_LITIGATION',
  noticeTypeId: '',
  actionTypeId: '',
  subject: '',
  bodyHtml: '',
  placeholders: '',
  statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const LANGUAGE_OPTIONS = [
  { value: 'EN', label: 'English' },
  { value: 'HI', label: 'Hindi' },
  { value: 'MR', label: 'Marathi' },
  { value: 'TA', label: 'Tamil' },
  { value: 'TE', label: 'Telugu' },
  { value: 'KN', label: 'Kannada' },
  { value: 'ML', label: 'Malayalam' },
  { value: 'BN', label: 'Bengali' },
  { value: 'GU', label: 'Gujarati' },
  { value: 'PA', label: 'Punjabi' },
];

export interface NoticeTemplateFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy' | 'view';
  record?: NoticeTemplate | null;
  onSaved: () => void;
  onClose: () => void;

  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const NoticeTemplateFormModal: React.FC<NoticeTemplateFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();
  const { options: caseTypeOptions } = useCaseTypeOptions();

  const [formData, setFormData] = useState<NoticeTemplateFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData); formDataRef.current = formData;
  const recordRef = useRef(record); recordRef.current = record;
  const initialSnapshotRef = useRef('');
  const modeRef = useRef(mode); modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const isRestoreRef = useRef(false);

  const [noticeTypeOptions, setNoticeTypeOptions] = useState<SelectOption[]>([]);
  const [actionTypeOptions, setActionTypeOptions] = useState<SelectOption[]>([]);
  const [seedNoticeTypeOption, setSeedNoticeTypeOption] = useState<SelectOption | null>(null);
  const [seedActionTypeOption, setSeedActionTypeOption] = useState<SelectOption | null>(null);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((f: string) => fieldErrors[f], [fieldErrors]);
  const clearFieldError = useCallback((f: string) => {
    setFieldErrors(p => { if (!p[f]) return p; const n = { ...p }; delete n[f]; return n; });
  }, []);

  // Paginated loaders (API caps pageSize at 100).
  const loadNoticeTypes = useCallback(async () => {
    try {
      const all: Array<{ id: string; noticeTypeCode: string; noticeTypeName: string }> = [];
      for (let page = 1; page <= 10; page++) {
        const res = await noticeTypeService.getAll({ page, pageSize: 100 });
        all.push(...res.data);
        if (!res.pagination.hasNextPage) break;
      }
      setNoticeTypeOptions(all.map(n => ({
        value: n.id,
        label: `${n.noticeTypeName} (${n.noticeTypeCode})`,
      })));
    } catch {
      setNoticeTypeOptions([]);
    }
  }, []);

  const loadActionTypes = useCallback(async () => {
    try {
      const all: Array<{ id: string; actionCode: string; actionName: string }> = [];
      for (let page = 1; page <= 10; page++) {
        const res = await actionTypeService.getAll({ page, pageSize: 100 });
        all.push(...res.data);
        if (!res.pagination.hasNextPage) break;
      }
      setActionTypeOptions(all.map(a => ({
        value: a.id,
        label: `${a.actionName} (${a.actionCode})`,
      })));
    } catch {
      setActionTypeOptions([]);
    }
  }, []);

  // Populate on open
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }

    loadNoticeTypes();
    loadActionTypes();

    const rec = recordRef.current;
    const data: NoticeTemplateFormData = rec
      ? {
          templateCode: rec.templateCode,
          templateName: rec.templateName,
          languageCode: rec.languageCode || 'EN',
          applicableCategory: rec.applicableCategory,
          noticeTypeId: rec.noticeTypeId,
          actionTypeId: rec.actionTypeId ?? '',
          subject: rec.subject ?? '',
          bodyHtml: rec.bodyHtml,
          placeholders: rec.placeholders ?? '',
          statusCode: (rec.statusCode === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE') as TemplateStatus,
        }
      : { ...INITIAL_FORM_DATA };

    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});

    // seedOptions for inactive FK labels on edit
    if (rec) {
      if (rec.noticeTypeId && (rec.noticeTypeCode || rec.noticeTypeName)) {
        setSeedNoticeTypeOption({
          value: rec.noticeTypeId,
          label: `${rec.noticeTypeName} (${rec.noticeTypeCode})${rec.noticeTypeStatusCode === 'INACTIVE' ? ' — inactive' : ''}`,
        });
      } else setSeedNoticeTypeOption(null);

      if (rec.actionTypeId && (rec.actionTypeCode || rec.actionTypeName)) {
        setSeedActionTypeOption({
          value: rec.actionTypeId,
          label: `${rec.actionTypeName} (${rec.actionTypeCode})${rec.actionTypeStatusCode === 'INACTIVE' ? ' — inactive' : ''}`,
        });
      } else setSeedActionTypeOption(null);
    } else {
      setSeedNoticeTypeOption(null);
      setSeedActionTypeOption(null);
    }
  }, [isOpen]);

  const combinedNoticeTypeOptions = useMemo(() => {
    if (!seedNoticeTypeOption) return noticeTypeOptions;
    const seedId = seedNoticeTypeOption.value;
    return [
      ...noticeTypeOptions.filter(o => o.value !== seedId),
      seedNoticeTypeOption,
    ];
  }, [noticeTypeOptions, seedNoticeTypeOption]);

  const combinedActionTypeOptions = useMemo(() => {
    const blank = [{ value: '', label: '— None —' }];
    if (!seedActionTypeOption) return [...blank, ...actionTypeOptions];
    const seedId = seedActionTypeOption.value;
    return [
      ...blank,
      ...actionTypeOptions.filter(o => o.value !== seedId),
      seedActionTypeOption,
    ];
  }, [actionTypeOptions, seedActionTypeOption]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true); else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentRecord = recordRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentRecord?.templateCode || currentFormData.templateCode || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.templateCode?.trim();
      const name = currentFormData.templateName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.templateCode || currentRecord?.templateCode || 'Template'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;

    minimize({
      id: modalId, title, moduleName, tabId, isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentRecord?.templateCode : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const handleSaveClick = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.templateCode.trim();
    const name = formData.templateName.trim();

    if (!code) errors.templateCode = 'Template code is required';
    else if (code.length > 30) errors.templateCode = 'Max 30 characters';
    else if (!/^[A-Z0-9-]+$/.test(code)) errors.templateCode = 'Uppercase letters, digits, hyphens only';

    if (!name) errors.templateName = 'Template name is required';
    else if (name.length > 200) errors.templateName = 'Max 200 characters';

    if (!formData.languageCode) errors.languageCode = 'Language is required';
    if (!formData.applicableCategory) errors.applicableCategory = 'Category is required';
    if (!formData.noticeTypeId) errors.noticeTypeId = 'Notice Type is required';
    if (!formData.bodyHtml.trim()) errors.bodyHtml = 'Body HTML is required';

    // Placeholders must be valid JSON if provided
    if (formData.placeholders.trim()) {
      try { JSON.parse(formData.placeholders); }
      catch { errors.placeholders = 'Placeholders must be valid JSON'; }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLElement>('input,textarea')?.focus();
        }
      });
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const code = formData.templateCode.trim().toUpperCase();
      const name = formData.templateName.trim();

      const payload = {
        templateCode: code,
        templateName: name,
        languageCode: formData.languageCode,
        applicableCategory: formData.applicableCategory,
        noticeTypeId: formData.noticeTypeId,
        actionTypeId: formData.actionTypeId || null,
        subject: formData.subject.trim() || null,
        bodyHtml: formData.bodyHtml,
        placeholders: formData.placeholders.trim() || null,
        statusCode: formData.statusCode,
      };

      if (mode === 'create' || mode === 'copy') {
        await noticeTemplateService.create(payload);
        notificationService.toast.success(`Notice Template "${code}" created successfully.`);
      } else if (record) {
        await noticeTemplateService.update(record.id, { ...payload, rowVersion: record.rowVersion });
        notificationService.toast.success(`Notice Template "${code}" updated successfully.`);
      }
      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      if (status === 409) {
        if (serverMsg?.toLowerCase().includes('modified')) {
          notificationService.toast.error('This template was modified by another user. Refresh and try again.', 6000);
        } else {
          notificationService.toast.error(serverMsg || 'A template with this code already exists.', 6000);
        }
      } else if (status === 400 && err?.response?.data?.errors?.length) {
        notificationService.toast.error((err.response.data.errors as string[]).join(' '), 6000);
      } else {
        notificationService.toast.error(serverMsg || 'Failed to save template. Please try again.', 6000);
      }
    }
  }, [formData, mode, record, onSaved, onClose]);

  const handleDiscard = useCallback(() => { setDiscardConfirmOpen(false); onClose(); }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem',
              border: '3px solid var(--border-color)', borderTopColor: '#2563eb',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{mode === 'view' ? 'View Notice Template' : mode === 'copy' ? 'Copy Notice Template' : mode === 'create' ? 'Add Notice Template' : 'Edit Notice Template'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <fieldset disabled={mode === 'view'} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
          <div className="form-grid-3">
            <div className="form-group" data-field="templateCode">
              <label className="form-label">Template Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('templateCode') ? ' form-field-error' : ''}`}
                value={formData.templateCode}
                maxLength={30}
                placeholder="DEMAND-138"
                onChange={(e) => { clearFieldError('templateCode'); setFormData(p => ({ ...p, templateCode: e.target.value.toUpperCase() })); }}
                disabled={isSaving}
              />
              {getFieldError('templateCode') && <div className="form-error-message">{getFieldError('templateCode')}</div>}
            </div>

            <div className="form-group" data-field="languageCode">
              <SyutSelect
                label={<>Language <span className="text-danger">*</span></>}
                required
                options={LANGUAGE_OPTIONS}
                value={formData.languageCode}
                onChange={(v) => { clearFieldError('languageCode'); setFormData(p => ({ ...p, languageCode: (v as string) || 'EN' })); }}
                error={getFieldError('languageCode')}
                isClearable={false}
                isDisabled={mode === 'view' || (isSaving)}
              />
            </div>

            <div className="form-group" data-field="applicableCategory">
              <SyutSelect
                label={<>Applicable Category <span className="text-danger">*</span></>}
                required
                options={caseTypeOptions}
                value={formData.applicableCategory}
                onChange={(v) => {
                  clearFieldError('applicableCategory');
                  setFormData(p => ({ ...p, applicableCategory: ((v as string) || 'PRE_LITIGATION') as ApplicableCategory }));
                }}
                error={getFieldError('applicableCategory')}
                isClearable={false}
                isDisabled={mode === 'view' || (isSaving)}
              />
            </div>
          </div>

          <div className="form-group" data-field="templateName">
            <label className="form-label">Template Name <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-input${getFieldError('templateName') ? ' form-field-error' : ''}`}
              value={formData.templateName}
              maxLength={200}
              placeholder="Section 138 Demand Notice — English"
              onChange={(e) => { clearFieldError('templateName'); setFormData(p => ({ ...p, templateName: e.target.value })); }}
              disabled={isSaving}
            />
            {getFieldError('templateName') && <div className="form-error-message">{getFieldError('templateName')}</div>}
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="noticeTypeId">
              <SyutSelect
                label={<>Notice Type <span className="text-danger">*</span></>}
                required
                options={combinedNoticeTypeOptions}
                value={formData.noticeTypeId}
                onChange={(v) => { clearFieldError('noticeTypeId'); setFormData(p => ({ ...p, noticeTypeId: (v as string) || '' })); }}
                error={getFieldError('noticeTypeId')}
                isClearable={false}
                placeholder="Select notice type"
                isDisabled={mode === 'view' || (isSaving)}
              />
            </div>

            <div className="form-group" data-field="actionTypeId">
              <SyutSelect
                label="Action Type"
                options={combinedActionTypeOptions}
                value={formData.actionTypeId}
                onChange={(v) => setFormData(p => ({ ...p, actionTypeId: (v as string) || '' }))}
                isClearable={false}
                placeholder="— None —"
                isDisabled={mode === 'view' || (isSaving)}
              />
            </div>
          </div>

          <div className="form-group" data-field="subject">
            <label className="form-label">Subject</label>
            <input
              type="text"
              className="form-input"
              value={formData.subject}
              maxLength={300}
              placeholder="Notice under Section 138..."
              onChange={(e) => setFormData(p => ({ ...p, subject: e.target.value }))}
              disabled={isSaving}
            />
          </div>

          <div className="form-group" data-field="bodyHtml">
            <label className="form-label">Body HTML <span className="text-danger">*</span></label>
            <textarea
              className={`form-textarea${getFieldError('bodyHtml') ? ' form-field-error' : ''}`}
              value={formData.bodyHtml}
              rows={12}
              placeholder="<p>Dear <b>{{borrower_name}}</b>,</p> <p>This is a statutory notice...</p>"
              onChange={(e) => { clearFieldError('bodyHtml'); setFormData(p => ({ ...p, bodyHtml: e.target.value })); }}
              disabled={isSaving}
              style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: '0.8125rem' }}
            />
            {getFieldError('bodyHtml') && <div className="form-error-message">{getFieldError('bodyHtml')}</div>}
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              Use <code>{'{{placeholder}}'}</code> tokens. HTML entities (<code>&amp;amp;</code>, <code>&lt;b&gt;</code>) are preserved.
            </div>
          </div>

          <div className="form-group" data-field="placeholders">
            <label className="form-label">Placeholders (JSON, optional)</label>
            <textarea
              className={`form-textarea${getFieldError('placeholders') ? ' form-field-error' : ''}`}
              value={formData.placeholders}
              rows={3}
              placeholder='{"placeholders":["borrower_name","amount"]}'
              onChange={(e) => { clearFieldError('placeholders'); setFormData(p => ({ ...p, placeholders: e.target.value })); }}
              disabled={isSaving}
              style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: '0.8125rem' }}
            />
            {getFieldError('placeholders') && <div className="form-error-message">{getFieldError('placeholders')}</div>}
          </div>

          <div className="form-group" data-field="statusCode">
            <SyutSelect
              label="Status"
              required
              options={STATUS_OPTIONS}
              value={formData.statusCode}
              onChange={(v) => setFormData(p => ({ ...p, statusCode: ((v as string) || 'ACTIVE') as TemplateStatus }))}
              isClearable={false}
              isDisabled={mode === 'view' || (isSaving)}
            />
          </div>
        </fieldset>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>{mode === 'view' ? 'Close' : 'Cancel'}</button>
          {mode !== 'view' && (<button className="btn btn-primary" onClick={handleSaveClick} disabled={isSaving}>
            {mode === 'copy' ? 'Save' : mode === 'create' ? 'Save' : 'Update'}
          </button>)}
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'copy' ? 'Confirm Create' : mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={(mode === 'create' || mode === 'copy')
          ? `Create notice template "${formData.templateCode.trim().toUpperCase()}"?`
          : `Update notice template "${formData.templateCode.trim().toUpperCase()}"?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={handleDiscard}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default NoticeTemplateFormModal;
